import {
  Modal,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalContainer,
  colors,
  spacing,
} from '@retailer-platform/shared-components'
import { Button } from '@retailer-platform/shared-components/src/tds/'
import { LoadingGenericBase } from '@instacart/ids-customers'
import React, { type CSSProperties, type FunctionComponent } from 'react'
import { useDomainMessages } from '../../utils/domain/intl'
import { Spacer } from '../../utils/common.styles.tsx'

interface Props {
  title?: string
  disabled?: boolean
  isLoading?: boolean
  onSubmit?: () => void
  onClose: () => void
  isFullscreen?: boolean
  showCancel?: boolean
  maxWidth?: number | string
  maxHeight?: number | string
  contentStylesOverride?: CSSProperties | undefined
  footerLeftSideComponent?: React.ReactNode
  subHeaderComponent?: React.ReactNode
}

export const QualityAuditWorkflowModal: FunctionComponent<React.PropsWithChildren<Props>> = ({
  title,
  onSubmit,
  isLoading,
  disabled,
  onClose,
  isFullscreen,
  showCancel = true,
  children,
  maxWidth,
  maxHeight,
  contentStylesOverride,
  footerLeftSideComponent,
  subHeaderComponent,
}) => {
  const i18n = useDomainMessages({
    cancel: 'catalogAdminDomain.qualityAuditWorkflows.tasks.modal.cancel',
    submit: 'catalogAdminDomain.qualityAuditWorkflows.tasks.modal.submit',
  })

  let modalMaxWidth = isFullscreen ? '100vw' : 480
  if (maxWidth) modalMaxWidth = maxWidth

  let modalMaxHeight = isFullscreen ? '100vh' : 1000
  if (maxHeight) modalMaxHeight = maxHeight

  return (
    <Modal
      isOpen={true}
      maxWidth={modalMaxWidth}
      maxHeight={modalMaxHeight}
      styleOverrides={
        isFullscreen
          ? {
              content: { borderRadius: 0, overflow: 'auto', ...(contentStylesOverride ?? {}) },
            }
          : {
              content: { overflow: 'auto', width: 'auto', ...(contentStylesOverride ?? {}) },
            }
      }
      hideCloseButton={true}
    >
      <ModalContainer
        style={isFullscreen ? { height: '100vh', width: '100vw', border: 0, borderRadius: 0 } : {}}
      >
        {title && <ModalHeader>{title}</ModalHeader>}
        {subHeaderComponent && (
          <ModalBody
            style={{
              display: 'flex',
              flexDirection: 'row',
              padding: `${spacing.X12}`,
              borderBottom: `1px solid ${colors.GRAYSCALE.X20}`,
              overflow: 'visible',
            }}
          >
            {subHeaderComponent}
          </ModalBody>
        )}
        <ModalBody>{children}</ModalBody>
        <ModalFooter>
          {isLoading && (
            <LoadingGenericBase
              // @ts-expect-error, this accepted but not properly typed
              css={{ backgroundColor: 'white' }}
            />
          )}
          {footerLeftSideComponent ? (
            <>
              {footerLeftSideComponent}
              <Spacer />
            </>
          ) : null}
          <Button.Group>
            {onSubmit && (
              <Button
                onClick={() => {
                  if (disabled) {
                    return
                  }
                  onSubmit()
                }}
                disabled={disabled}
              >
                {i18n.submit}
              </Button>
            )}
            {showCancel && (
              <Button
                variant={'secondary'}
                onClick={() => {
                  if (onClose) onClose()
                }}
              >
                {i18n.cancel}
              </Button>
            )}
          </Button.Group>
        </ModalFooter>
      </ModalContainer>
    </Modal>
  )
}
